import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Brain, Lock, CheckCircle, XCircle, TrendingDown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface AnxietyEntry {
  id: string;
  timestamp: string;
  relationshipId: string;
  anxiousThought: string;
  prediction: string;
  actualOutcome?: string;
  outcomeTimestamp?: string;
  accuracy: 'accurate' | 'inaccurate' | 'pending';
  severity: number; // 1-10
}

interface AnxietyVsRealityProps {
  relationshipId: string;
  tier: 'free' | 'premium' | 'pro';
}

export function AnxietyVsReality({ relationshipId, tier }: AnxietyVsRealityProps) {
  const [anxiousThought, setAnxiousThought] = useState('');
  const [prediction, setPrediction] = useState('');
  const [severity, setSeverity] = useState(5);
  const [entries, setEntries] = useState<AnxietyEntry[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);

  const isPro = tier === 'pro';

  useEffect(() => {
    loadEntries();
  }, [relationshipId]);

  const loadEntries = () => {
    const stored = localStorage.getItem('tension_anxiety_tracker');
    if (stored) {
      const all = JSON.parse(stored);
      const filtered = all.filter((e: AnxietyEntry) => e.relationshipId === relationshipId);
      setEntries(filtered);
    }
  };

  const handleSaveAnxiety = () => {
    if (!anxiousThought.trim() || !prediction.trim()) {
      toast.error('Please fill in both fields');
      return;
    }

    const entry: AnxietyEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      relationshipId,
      anxiousThought: anxiousThought.trim(),
      prediction: prediction.trim(),
      accuracy: 'pending',
      severity,
    };

    const stored = localStorage.getItem('tension_anxiety_tracker') || '[]';
    const all = JSON.parse(stored);
    all.push(entry);
    localStorage.setItem('tension_anxiety_tracker', JSON.stringify(all));

    toast.success('Anxiety logged. We\'ll check back in 48 hours.');
    setAnxiousThought('');
    setPrediction('');
    setSeverity(5);
    loadEntries();
  };

  const handleUpdateOutcome = (entryId: string, outcome: string, accuracy: 'accurate' | 'inaccurate') => {
    const stored = localStorage.getItem('tension_anxiety_tracker') || '[]';
    const all = JSON.parse(stored);
    const updated = all.map((e: AnxietyEntry) => 
      e.id === entryId 
        ? { 
            ...e, 
            actualOutcome: outcome,
            outcomeTimestamp: new Date().toISOString(),
            accuracy 
          }
        : e
    );
    localStorage.setItem('tension_anxiety_tracker', JSON.stringify(updated));
    toast.success('Outcome recorded');
    loadEntries();
    setSelectedEntry(null);
  };

  const calculateAccuracyRate = () => {
    const resolved = entries.filter(e => e.accuracy !== 'pending');
    if (resolved.length === 0) return null;
    
    const accurate = resolved.filter(e => e.accuracy === 'accurate').length;
    const rate = Math.round((accurate / resolved.length) * 100);
    return { rate, total: resolved.length, accurate };
  };

  const getPendingEntries = () => {
    return entries.filter(e => e.accuracy === 'pending');
  };

  const accuracyStats = calculateAccuracyRate();
  const pendingEntries = getPendingEntries();

  const getInsight = () => {
    if (!accuracyStats || accuracyStats.total < 3) return null;

    if (accuracyStats.rate < 30) {
      return {
        type: 'success',
        message: `Your anxious thoughts were wrong ${100 - accuracyStats.rate}% of the time. Your anxiety is lying to you.`,
        icon: TrendingDown,
      };
    } else if (accuracyStats.rate < 60) {
      return {
        type: 'neutral',
        message: 'Your predictions are mixed. Practice distinguishing intuition from anxiety.',
        icon: AlertCircle,
      };
    } else {
      return {
        type: 'warning',
        message: 'Your predictions are often accurate. This may indicate real relationship issues worth addressing.',
        icon: AlertCircle,
      };
    }
  };

  const insight = getInsight();

  if (!isPro) {
    return (
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Lock className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
              <h3 className="font-medium text-neutral-900">Anxiety vs. Reality Tracker</h3>
            </div>
            <p className="text-sm text-neutral-600 mb-3">
              Log anxious thoughts and predictions, then track whether they came true. Most anxious predictions are wrong—see the data.
            </p>
            <div className="bg-white/60 rounded-lg p-3 mb-3">
              <p className="text-xs text-neutral-700 mb-2">
                <strong>Example:</strong>
              </p>
              <p className="text-xs text-neutral-600 italic mb-1">
                "They haven't responded in 3 hours. I predict they're losing interest."
              </p>
              <p className="text-xs text-green-700 mt-2">
                <strong>48 hours later:</strong> "They responded warmly, just had a busy day at work."
              </p>
              <p className="text-xs text-neutral-600 mt-2">
                Result: Anxiety was <strong>inaccurate</strong> ✓
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => window.location.href = '/settings'}
              className="gap-2"
            >
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            <h3 className="font-medium text-neutral-900">Anxiety vs. Reality Tracker</h3>
            <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
          </div>

          <p className="text-sm text-neutral-600">
            Log your anxious thoughts and predictions. We'll help you track if they come true.
          </p>

          {/* Log New Anxiety */}
          <div className="space-y-3">
            <div>
              <label className="text-xs text-neutral-700 font-medium mb-1 block">
                What are you anxious about right now?
              </label>
              <Textarea
                value={anxiousThought}
                onChange={(e) => setAnxiousThought(e.target.value)}
                placeholder="e.g., They haven't responded in 3 hours..."
                maxLength={200}
                rows={2}
                className="resize-none"
              />
              <span className="text-xs text-neutral-500">{anxiousThought.length}/200</span>
            </div>

            <div>
              <label className="text-xs text-neutral-700 font-medium mb-1 block">
                What do you predict will happen?
              </label>
              <Textarea
                value={prediction}
                onChange={(e) => setPrediction(e.target.value)}
                placeholder="e.g., I think they're losing interest and will pull away..."
                maxLength={200}
                rows={2}
                className="resize-none"
              />
              <span className="text-xs text-neutral-500">{prediction.length}/200</span>
            </div>

            <div>
              <label className="text-xs text-neutral-700 font-medium mb-1 block">
                Anxiety Severity (1-10)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={severity}
                  onChange={(e) => setSeverity(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-neutral-900 w-8">{severity}</span>
              </div>
            </div>

            <Button onClick={handleSaveAnxiety} className="w-full">
              Log Anxiety
            </Button>
          </div>

          {/* Accuracy Stats */}
          {accuracyStats && (
            <div className="bg-white/60 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-900">Your Anxiety Accuracy</span>
                <span className="text-2xl font-light text-purple-600">{accuracyStats.rate}%</span>
              </div>
              <div className="bg-neutral-200 rounded-full h-2 mb-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{ width: `${accuracyStats.rate}%` }}
                />
              </div>
              <p className="text-xs text-neutral-600">
                {accuracyStats.accurate} of {accuracyStats.total} predictions were accurate
              </p>
            </div>
          )}

          {/* Insight */}
          {insight && (
            <div className={`rounded-lg p-4 border ${
              insight.type === 'success' 
                ? 'bg-green-50 border-green-200' 
                : insight.type === 'warning'
                ? 'bg-orange-50 border-orange-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start gap-3">
                <insight.icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  insight.type === 'success' 
                    ? 'text-green-600' 
                    : insight.type === 'warning'
                    ? 'text-orange-600'
                    : 'text-blue-600'
                }`} />
                <p className={`text-sm ${
                  insight.type === 'success' 
                    ? 'text-green-900' 
                    : insight.type === 'warning'
                    ? 'text-orange-900'
                    : 'text-blue-900'
                }`}>
                  {insight.message}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Pending Check-ins */}
      {pendingEntries.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
          <div className="space-y-3">
            <h4 className="font-medium text-neutral-900">Pending Reality Checks</h4>
            <p className="text-sm text-neutral-600">
              It's been 48+ hours. What actually happened?
            </p>
            {pendingEntries.map((entry) => {
              const hoursSince = Math.floor(
                (new Date().getTime() - new Date(entry.timestamp).getTime()) / (1000 * 60 * 60)
              );

              return (
                <div key={entry.id} className="bg-white rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-neutral-500 mb-1">
                        {new Date(entry.timestamp).toLocaleDateString()} ({hoursSince}h ago)
                      </p>
                      <p className="text-sm text-neutral-900 font-medium mb-1">
                        Anxious thought:
                      </p>
                      <p className="text-sm text-neutral-700 italic mb-2">"{entry.anxiousThought}"</p>
                      <p className="text-sm text-neutral-900 font-medium mb-1">
                        Prediction:
                      </p>
                      <p className="text-sm text-neutral-700 italic">"{entry.prediction}"</p>
                    </div>
                  </div>

                  {selectedEntry === entry.id ? (
                    <div className="space-y-3 pt-3 border-t border-neutral-200">
                      <Textarea
                        placeholder="What actually happened?"
                        rows={2}
                        id={`outcome-${entry.id}`}
                        className="resize-none"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
                          onClick={() => {
                            const textarea = document.getElementById(`outcome-${entry.id}`) as HTMLTextAreaElement;
                            handleUpdateOutcome(entry.id, textarea.value, 'accurate');
                          }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Prediction Was Accurate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                          onClick={() => {
                            const textarea = document.getElementById(`outcome-${entry.id}`) as HTMLTextAreaElement;
                            handleUpdateOutcome(entry.id, textarea.value, 'inaccurate');
                          }}
                        >
                          <XCircle className="w-4 h-4" />
                          Anxiety Was Wrong
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedEntry(null)}
                        className="w-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => setSelectedEntry(entry.id)}
                      className="w-full"
                    >
                      Update with Reality
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* History */}
      {entries.filter(e => e.accuracy !== 'pending').length > 0 && (
        <Card className="p-6">
          <div className="space-y-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between"
            >
              <h4 className="font-medium text-neutral-900">History</h4>
              <span className="text-sm text-neutral-600">
                {showHistory ? 'Hide' : 'Show'} {entries.filter(e => e.accuracy !== 'pending').length} entries
              </span>
            </button>

            {showHistory && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {entries
                  .filter(e => e.accuracy !== 'pending')
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  .map((entry) => (
                    <div
                      key={entry.id}
                      className={`rounded-lg p-4 border ${
                        entry.accuracy === 'inaccurate'
                          ? 'bg-purple-50 border-purple-200'
                          : 'bg-orange-50 border-orange-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs text-neutral-500">
                          {new Date(entry.timestamp).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-1">
                          {entry.accuracy === 'inaccurate' ? (
                            <>
                              <XCircle className="w-4 h-4 text-purple-600" />
                              <span className="text-xs text-purple-700 font-medium">Anxiety Was Wrong</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 text-orange-600" />
                              <span className="text-xs text-orange-700 font-medium">Prediction Accurate</span>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-neutral-600 mb-1">Thought: "{entry.anxiousThought}"</p>
                      <p className="text-xs text-neutral-600 mb-1">Predicted: "{entry.prediction}"</p>
                      {entry.actualOutcome && (
                        <p className="text-xs text-neutral-900 mt-2">
                          <strong>Reality:</strong> "{entry.actualOutcome}"
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
